import React, {useState} from "react";
import {useSelector} from "react-redux";
import {usePermission} from "./PermissionHooks";
import {service as ContextNodeService} from "../Services/ContextNodeService";
import {service as UserRoleService} from "../Services/UserRoleService";
import {usePrimeCache} from "./ServiceHooks";
import jmespath from "jmespath";
import {GenericModelUtils} from "../Utils/GenericModelUtils";

export const useGenerateGuid = () => {
    return () => {
        let s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
        return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
    }
}

export const useValidations = () => {
    // const [isValid, setIsValid] = useState(false);
    const [validations, setValidations] = useState([]);

    const isValid = () => {
        for (const propertyName in validations) {
            const validation = validations[propertyName];

            if (!validation.valid) {
                return false;
            }
        }
        return true;
    }

    // useEffect(() => {
    //     for (const propertyName in validations) {
    //         const validation = validations[propertyName];
    //
    //         if (!validation.valid) {
    //             setIsValid(false);
    //         }
    //     }
    //     setIsValid(true);
    // }, [validations])

    return [
        validations,
        (validations) => setValidations({...validations}),
        isValid
    ];

}
export const useBuildContextNodeEditabilityElement = () => {

    usePrimeCache([UserRoleService]);
    const user = useSelector(state => state.userState.user);
    const userRole = UserRoleService.CachedValueById(user?.userRoleId)?.result;

    return (contextNode) => {
        if(userRole == null || contextNode?.depth == null) {
            return null;
        }

        const editabilityMessage = ContextNodeService.GetEditabilityMessage(userRole, contextNode.depth);
        const editabilityElement = editabilityMessage ? <div className="sb-autocomplete-subtext">{editabilityMessage}</div> : null;

        return editabilityElement;
    }
}

export const useShouldEditField = () => {
    const permission = usePermission();

    return (props) => {
        // TODO: do we need to store these in state and
        // return a different thing when they change?
        const {EditMode, FieldDef, IsNewModel} = props;

        // Check the User Permissions
        if (
            FieldDef?.requiresPermission &&
            !permission(FieldDef?.requiresPermission)
        ) {
            return false;
        }

        return EditMode;
    }
}

export const useGenericModelHooks = (props) => {

    const {
        GetFieldDefs: getFieldDefs = (() => {}),
        UpdateField: updateField = (() => {}),
        UpdateFields: updateFields = (() => {}),
    } = props;

    const sortFieldDefs = (defs) => {
        if(defs) {
            defs = defs.sort((a, b) => {
                if(a.displayOrder && b.displayOrder) {
                    return a.displayOrder - b.displayOrder;
                } else if(a.displayOrder) {
                    return -1;
                } else if(b.displayOrder) {
                    return 1;
                } else {
                    return a.key.localeCompare(b.key);
                }
            });

            return defs;
        }

        return [];
    }

    return {
        getFieldDefs,
        updateField,
        updateFields,
        sortFieldDefs,
    }
}

export const useFieldDefinitionVisibility = (props) => {

    const hasPermissions = usePermission();
    const {
        EditMode,
        HasPermission,
        Data,
        RootData,
        CustomData
    } = props;

    const fieldDefinitionIsVisible = (field, hiddenFieldKeys = []) => {
        // primary key is always hidden
        // if the user could change this they
        // might overwrite other data unintentionally
        if(field.isPrimaryKey) {
            return false;
        }

        // if this field is a child of an association
        // it should be hidden if the association is hidden
        if(field.association && !fieldDefinitionIsVisible(field.association, hiddenFieldKeys)) {
            return false;
        }

        // hide based on viewerOnly and editorOnly rules
        if((EditMode ? field.viewerOnly : field.editorOnly)) {
            return false;
        }

        // hide nullable associations {
        if(GenericModelUtils.isHiddenNullableAssociationField(field, Data)) {
            return false;
        }

        // hide fields that are in the hiddenFieldKeys list
        if(hiddenFieldKeys?.includes?.(field.key)) {
            return false;
        }

        let isHidden = false;

        if(typeof field.isHidden == 'object') {
            const { query, requiresPermission } = field.isHidden;
            const searchContext = {
                ...Data,
                __RootData: RootData,
                __CustomData: CustomData,
            }

            if(query) {
                isHidden = isHidden || jmespath.search(searchContext, query);
            }
            if(requiresPermission) {
                isHidden = isHidden || !hasPermissions(requiresPermission);
            }
        } else {
            isHidden = isHidden || field.isHidden;
        }

        return !isHidden;
    }

    return (groups, hiddenGroupKeys = [], hiddenFieldKeys = []) => groups
        .filter(group => (
            !hiddenGroupKeys ||
            !(hiddenGroupKeys.includes(group.key))
        ))
        .map(group => ({
            ...group,
            value: group.value.filter(
                fieldDef => fieldDefinitionIsVisible(fieldDef, hiddenFieldKeys)
            )
        })
    );
}

